import {Component, OnInit} from '@angular/core';
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {ActivatedRoute, Router} from "@angular/router";
import {take} from "rxjs";
import {AccessService} from "../../../../service/api/access.service";
import {MessageToastService} from "../../../../service/message-toast.service";
import {ErrorMessageEnum} from "../../../../shared/enums/error-message.enum";
import {ApiResponse} from "../../../../shared/model/response/ApiResponse";

@Component({
    selector: 'app-recover-password',
    templateUrl: './recover-password.component.html',
    standalone: false
})
export class RecoverPasswordComponent implements OnInit {

  step: 'request' | 'confirm' = 'request'
  form: FormGroup
  formErrors: Map<string, string> = new Map()

  constructor(private accessService: AccessService,
              private messageToastService: MessageToastService,
              private route: ActivatedRoute,
              private router: Router) {
  }

  ngOnInit(): void {
    this.form = new FormGroup({
      "phoneNumber": new FormControl(null, [Validators.required, Validators.pattern(/^\d{9}$/)]),
      "pin": new FormControl(null),
      "password": new FormControl(null),
      "passwordRe": new FormControl(null),
    })

    this.route.queryParams.pipe(take(1)).subscribe(params => {
      if (params['fase'] === 'pin') {
        this.step = 'confirm'
      }
    })
  }

  showStep(step: 'request' | 'confirm') {
    this.step = step
    this.formErrors.clear()
  }

  onRequestPin() {
    this.formErrors.clear()
    if (!this.phoneIsValid()) return

    this.accessService.passwordRecoveryRequest(this.form.value.phoneNumber).subscribe({
      next: (response) => {
        this.messageToastService.success(response.message)
        this.showStep('confirm')
      },
      error: (err: ApiResponse<any>) => {
        this.formErrors = err?.formErrors ?? new Map()
        this.messageToastService.error(err?.message)
      }
    })
  }

  onConfirmPin() {
    this.formErrors.clear()
    if (!this.phoneIsValid() || !this.pinIsValid() || !this.passwordsAreValid()) return

    this.accessService.changePassword({
      phoneNumber: this.form.value.phoneNumber,
      pin: String(this.form.value.pin),
      password: this.form.value.password,
      passwordRe: this.form.value.passwordRe
    }).subscribe({
      next: (response) => {
        this.router.navigate(["/acesso"]).then(() => this.messageToastService.success(response.message))
      },
      error: (err: ApiResponse<any>) => {
        this.formErrors = err?.formErrors ?? new Map()
        this.messageToastService.error(err?.message)
      }
    })
  }

  private phoneIsValid(): boolean {
    const phone = this.form.get("phoneNumber")
    if (phone?.hasError("required")) {
      this.formErrors.set('phoneNumber', ErrorMessageEnum.requiredField)
      return false
    }
    if (phone?.hasError("pattern")) {
      this.formErrors.set('phoneNumber', ErrorMessageEnum.invalidPhone)
      return false
    }
    return true
  }

  private pinIsValid(): boolean {
    const pin = String(this.form.value.pin ?? '')
    if (!pin) {
      this.formErrors.set('pin', ErrorMessageEnum.requiredField)
      return false
    }
    if (!/^\d{5}$/.test(pin)) {
      this.formErrors.set('pin', ErrorMessageEnum.invalidPin)
      return false
    }
    return true
  }

  private passwordsAreValid(): boolean {
    const password = this.form.value.password
    const passwordRe = this.form.value.passwordRe
    let valid = true

    if (!password) {
      this.formErrors.set('password', ErrorMessageEnum.requiredField)
      valid = false
    }
    if (!passwordRe) {
      this.formErrors.set('passwordRe', ErrorMessageEnum.requiredField)
      valid = false
    }
    if (password && passwordRe && password !== passwordRe) {
      this.formErrors.set('password', ErrorMessageEnum.passwordMismatch)
      this.formErrors.set('passwordRe', ErrorMessageEnum.passwordMismatch)
      valid = false
    }
    return valid
  }
}
